import { dateTimeFormatter, DEFAULT_TIME_FORMAT } from '@idu-nightlife/nightlife-utils';
import { Box } from '@mui/material';
import { Typography } from '../../primitives/typography/typography';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getHealthOptions } from '../../../@api-client/@tanstack/react-query.gen';
import { AppVersionData, getAppVersionData } from './version-utils';
import { DualLineTooltip } from '../../primitives/dual-line-tooltip';

const CACHE_TIME = 30 * 1000;

type VersionDataProps = {
  layout?: 'compact' | 'inline' | 'stacked';
};

export const VersionData = ({ layout = 'stacked' }: VersionDataProps) => {
  const { data: healthData, isLoading: isHealthLoading } = useQuery({
    ...getHealthOptions(),
    staleTime: CACHE_TIME,
  });

  const [currentTime, setCurrentTime] = useState('');
  const [versionData, setVersionData] = useState<AppVersionData>({
    runningVersion: '-',
    nativeVersion: 'N/A',
    webViewPath: '',
    isNative: false,
  });

  useEffect(() => {
    let isMounted = true;
    void getAppVersionData().then((data) => {
      if (isMounted) setVersionData(data);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const formattedTime = dateTimeFormatter({
        value: now,
        mode: 'time',
        timeFormat: DEFAULT_TIME_FORMAT,
      });
      setCurrentTime(formattedTime);
    }, CACHE_TIME);

    const now = new Date();
    const formattedTime = dateTimeFormatter({ value: now, mode: 'time' });
    setCurrentTime(formattedTime);

    return () => clearInterval(interval);
  }, []);

  const apiVersion = isHealthLoading ? '...' : (healthData?.data.version ?? 'N/A');
  const { runningVersion, nativeVersion } = versionData;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isCompact = layout === 'compact';

  const hasDifferentNativeVersion = nativeVersion && nativeVersion !== runningVersion;

  const versionCaption = (
    <>
      Code v{runningVersion}
      {hasDifferentNativeVersion && <> (Native v{nativeVersion})</>}
      {' • '}API v{apiVersion}
      {layout === 'stacked' ? <br /> : ' • '}
      {timeZone} {currentTime}
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <DualLineTooltip
        title="Version information"
        body={
          <Box sx={{ display: 'block' }}>
            Running: {runningVersion} (Active code)
            <br />
            Native: {nativeVersion || 'N/A'} (Binary)
            <br />
            API: {apiVersion}
            <br />
            Local time: {timeZone} {currentTime}
          </Box>
        }
      >
        <Typography.Caption>
          {isCompact ? `Version ${runningVersion} | ${apiVersion}` : versionCaption}
        </Typography.Caption>
      </DualLineTooltip>
    </Box>
  );
};
